import { useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AccessDenied from "@/components/AccessDenied";
import OrgTemplates from "@/pages/OrgTemplates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Building2, UserPlus, Trash2, Copy, Users, Send, MailPlus, Crown, ShieldCheck, Download, BadgeCheck, MailWarning, Pencil, ChevronUp, ChevronDown, ChevronRight, Network, Search, Plus, Minus } from "lucide-react";

const parseCsvLine = (line) => {
  const out = []; let cur = ""; let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (q) {
      if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; }
      else cur += ch;
    } else if (ch === '"') q = true;
    else if (ch === ",") { out.push(cur); cur = ""; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim());
};

const parseCsv = (text) => {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  let rows = lines.map(parseCsvLine);
  const header = rows[0].map((h) => h.toLowerCase());
  const hasHeader = header.some((h) => h.includes("email"));
  let idx = { name: 0, email: 1, role: 2, dept: 3 };
  if (hasHeader) {
    idx = {
      name: header.findIndex((h) => h.includes("name")),
      email: header.findIndex((h) => h.includes("email")),
    };
    rows = rows.slice(1);
  }
  const pick = (r, i) => (i >= 0 && i < r.length ? r[i] : "");
  return rows
    .map((r) => ({
      full_name: pick(r, idx.name),
      email: pick(r, idx.email),
    }))
    .filter((m) => m.email);
};

export default function OrgAdmin() {
  const { t } = useTranslation();
  const { user: authUser, refresh } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [billingHealth, setBillingHealth] = useState(null);
  const [members, setMembers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newMember, setNewMember] = useState({ email: "", full_name: "", supervisor_id: "", title: "", is_org_admin: false });
  const [titleLadder, setTitleLadder] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [removeTarget, setRemoveTarget] = useState(null);
  const [reassignTo, setReassignTo] = useState("");
  const [removeMode, setRemoveMode] = useState("vacant");
  const [moveTeamTarget, setMoveTeamTarget] = useState(null);
  const [moveTeamTo, setMoveTeamTo] = useState("");
  const [tempPw, setTempPw] = useState(null);
  const [digestRecipients, setDigestRecipients] = useState([]);
  const [digestCadence, setDigestCadence] = useState("monthly");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkEmails, setBulkEmails] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [csvRows, setCsvRows] = useState(null);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [langFilter, setLangFilter] = useState("all");
  const [transferBusy, setTransferBusy] = useState(false);
  const [transferPassword, setTransferPassword] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferPick, setTransferPick] = useState("");
  const [recoveryRequests, setRecoveryRequests] = useState([]);
  const [ownershipLog, setOwnershipLog] = useState([]);
  const [accessLog, setAccessLog] = useState([]);
  const [accessFilterMember, setAccessFilterMember] = useState("all");
  const [accessFilterScope, setAccessFilterScope] = useState("all");
  const [expiryDays, setExpiryDays] = useState(7);
  const [renameOpen, setRenameOpen] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [nameBusy, setNameBusy] = useState(false);
  const [showAccessHistory, setShowAccessHistory] = useState(false);
  const [pendingReorg, setPendingReorg] = useState(null);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestPreviewLoading, setDigestPreviewLoading] = useState(false);
  const [confirmPerm, setConfirmPerm] = useState(null);
  const [previewFor, setPreviewFor] = useState(null);
  const [pv, setPv] = useState({ action: "grant", is_org_admin: true, lang: "en" });
  const [pvData, setPvData] = useState({ subject: "", html: "", loading: false });

  const load = async () => {
    setLoadError(null);
    try {
      const [s, m] = await Promise.all([api.get("/org/settings"), api.get("/org/members")]);
      setSettings(s.data); setMembers(m.data);
      setTitleLadder(s.data.title_ladder || []);
      setDigestRecipients(s.data.digest_recipients || []);
      setDigestCadence(s.data.digest_cadence || "monthly");
      setExpiryDays(s.data.invite_expiry_days || 7);
      api.get("/org/ownership-log").then((r) => setOwnershipLog(r.data)).catch(() => {});
      api.get("/org/access-log").then((r) => setAccessLog(r.data)).catch(() => {});
      api.get("/org/primary-admin/recovery-requests").then((r) => setRecoveryRequests(r.data)).catch(() => {});
      api.get("/billing/org_dunning").then((r) => setBillingHealth(r.data)).catch(() => {});
    } catch (err) {
      const msg = apiError(err);
      setLoadError(msg);
      toast.error(msg);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleRecipient = (id) => setDigestRecipients((r) => r.includes(id) ? r.filter((x) => x !== id) : [...r, id]);
  const saveRecipients = async () => {
    try {
      await api.put("/org/digest-recipients", { user_ids: digestRecipients });
      await api.put("/org/digest-cadence", { cadence: digestCadence });
      toast.success(t("orgadmin.toasts.digestSaved"));
    } catch (err) { toast.error(apiError(err)); }
  };

  const addMember = async () => {
    try {
      const payload = { email: newMember.email, full_name: newMember.full_name, supervisor_id: newMember.supervisor_id || null, title: newMember.title || null, is_org_admin: !!newMember.is_org_admin };
      const res = await api.post("/org/members", payload);
      setTempPw({ email: res.data.member.email, pw: res.data.temp_password, emailSent: res.data.email_sent });
      setAdding(false);
      setNewMember({ email: "", full_name: "", supervisor_id: "", title: "", is_org_admin: false });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const updateMember = async (id, field, value) => {
    try {
      await api.put(`/org/members/${id}`, { [field]: value });
      load();
      if (field === "language" && id === authUser?.id) refresh();
    }
    catch (err) { toast.error(apiError(err)); }
  };

  const removeMember = async (id, opts = {}) => {
    try {
      let url = `/org/members/${id}`;
      if (opts.vacant) url += `?leave_vacant=true`;
      else if (opts.reassign) url += `?reassign_to=${opts.reassign}`;
      await api.delete(url);
      toast.success(t("orgadmin.toasts.memberRemoved"));
      setRemoveTarget(null); setReassignTo(""); setRemoveMode("vacant"); load();
    } catch (err) {
      if (err.response?.status === 409) { setRemoveTarget(members.find((m) => m.id === id) || { id }); setReassignTo(""); setRemoveMode("vacant"); }
      else toast.error(apiError(err));
    }
  };

  const saveTitles = async () => {
    try {
      const res = await api.put("/org/titles", { titles: titleLadder });
      setTitleLadder(res.data.title_ladder);
      toast.success(t("orgadmin.titlesSaved"));
    } catch (err) { toast.error(apiError(err)); }
  };
  const addTitle = () => {
    const v = newTitle.trim();
    if (!v || titleLadder.some((x) => (x.title || "").toLowerCase() === v.toLowerCase())) return;
    setTitleLadder([...titleLadder, { title: v, grade: "" }]); setNewTitle("");
  };
  const moveTitle = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= titleLadder.length) return;
    const next = [...titleLadder]; [next[i], next[j]] = [next[j], next[i]]; setTitleLadder(next);
  };
  const updateRung = (i, field, value) => setTitleLadder(titleLadder.map((x, idx) => idx === i ? { ...x, [field]: value } : x));
  const gradeForTitle = (title) => {
    if (!title) return null;
    const r = titleLadder.find((x) => (x.title || "").toLowerCase() === String(title).toLowerCase());
    return (r && r.grade) || null;
  };

  const reportCount = (id) => members.filter((x) => x.supervisor_id === id).length;
  const moveTeam = async () => {
    try {
      const res = await api.post(`/org/members/${moveTeamTarget.id}/reassign-reports`, { new_manager_id: moveTeamTo || null });
      toast.success(t("orgadmin.teamMoved", { count: res.data.reassigned }));
      setMoveTeamTarget(null); setMoveTeamTo(""); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  // Build reports-to tree for the Org chart
  const [dragId, setDragId] = useState(null);
  const dragIdRef = useRef(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [chartSearch, setChartSearch] = useState("");
  const [collapsed, setCollapsed] = useState(new Set());
  const [zoom, setZoom] = useState(1);
  const chartRef = useRef(null);
  const viewportRef = useRef(null);
  const panRef = useRef({ active: false, x: 0, y: 0, sl: 0, st: 0 });
  const [structureSection, setStructureSection] = useState("titles");
  const [focusId, setFocusId] = useState(null);
  const toggleCollapse = (id) => setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const isDescendant = (childrenMap, ancestorId, nodeId) => {
    const kids = childrenMap[ancestorId] || [];
    for (const c of kids) {
      if (c.id === nodeId || isDescendant(childrenMap, c.id, nodeId)) return true;
    }
    return false;
  };
  const subtreeCount = (childrenMap, id) => {
    const kids = childrenMap[id] || [];
    return kids.reduce((n, c) => n + 1 + subtreeCount(childrenMap, c.id), 0);
  };
  const applyManager = async (memberId, newManagerId) => {
    await api.put(`/org/members/${memberId}`, { supervisor_id: newManagerId || null });
    load();
  };
  const reorg = async (memberId, newManagerId, childrenMap, isUndo = false) => {
    const m = members.find((x) => x.id === memberId);
    if (!m) return;
    const prevManagerId = m.supervisor_id || null;
    if (prevManagerId === (newManagerId || null)) return;
    if (newManagerId && (newManagerId === memberId || isDescendant(childrenMap, memberId, newManagerId))) {
      toast.error(t("orgadmin.reorgLoop")); return;
    }
    try {
      await applyManager(memberId, newManagerId);
      const teamSize = subtreeCount(childrenMap, memberId);
      const base = newManagerId ? t("orgadmin.reorgDone", { name: m.full_name }) : t("orgadmin.reorgToTop", { name: m.full_name });
      const msg = teamSize > 0 ? `${base} ${t("orgadmin.reorgWithTeam", { count: teamSize })}` : base;
      if (isUndo) { toast.success(t("orgadmin.reorgUndone")); return; }
      toast.success(msg, {
        action: {
          label: t("common.undo"),
          onClick: () => reorg(memberId, prevManagerId, childrenMap, true),
        },
      });
    } catch (err) { toast.error(apiError(err)); }
  };
  const exportChart = async (fmt) => {
    if (!chartRef.current) return;
    setExporting(true);
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(chartRef.current, { backgroundColor: "#ffffff", scale: 2 });
      const orgName = (settings?.name || "org").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      if (fmt === "png") {
        const link = document.createElement("a");
        link.download = `${orgName}-org-chart.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
      } else {
        const { jsPDF } = await import("jspdf");
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({ orientation: canvas.width > canvas.height ? "landscape" : "portrait", unit: "pt", format: "a4" });
        const pw = pdf.internal.pageSize.getWidth();
        const ph = pdf.internal.pageSize.getHeight();
        const ratio = Math.min((pw - 40) / canvas.width, (ph - 40) / canvas.height);
        const w = canvas.width * ratio, h = canvas.height * ratio;
        pdf.addImage(imgData, "PNG", (pw - w) / 2, 20, w, h);
        pdf.save(`${orgName}-org-chart.pdf`);
      }
      toast.success(t("orgadmin.chartExported"));
    } catch (err) { toast.error(t("orgadmin.chartExportError")); }
    finally { setExporting(false); }
  };
  const titleRank = (title) => {
    if (!title) return -1;
    return titleLadder.findIndex((x) => (x.title || "").toLowerCase() === String(title).toLowerCase());
  };
  const attemptReorg = (memberId, newManagerId, childrenMap) => {
    if (!newManagerId) { reorg(memberId, null, childrenMap); return; }
    const member = members.find((x) => x.id === memberId);
    const mgr = members.find((x) => x.id === newManagerId);
    const rm = titleRank(member?.title), rg = titleRank(mgr?.title);
    // Soft warning: reporting to an equal or lower title (both must be on the ladder).
    if (rm >= 0 && rg >= 0 && rm >= rg) {
      setPendingReorg({ memberId, newManagerId, childrenMap,
        memberName: member?.full_name, mgrName: mgr?.full_name,
        memberTitle: member?.title, mgrTitle: mgr?.title });
      return;
    }
    reorg(memberId, newManagerId, childrenMap);
  };

  const loadDigestPreview = async () => {
    setDigestPreviewLoading(true);
    try {
      const res = await api.get("/org/digest-preview");
      setDigestPreview(res.data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setDigestPreviewLoading(false); }
  };

  const nodeIdsWithKids = () => {
    const { children } = buildTree();
    return Object.keys(children).filter((id) => (children[id] || []).length > 0);
  };
  const toggleCollapseAll = () => setCollapsed((c) => (c.size > 0 ? new Set() : new Set(nodeIdsWithKids())));

  const onPanDown = (e) => {
    const vp = viewportRef.current; if (!vp) return;
    if (e.target.closest(".orgchart-node") || e.target.closest("button") || e.target.closest("input") || e.target.closest("[data-testid='chart-drop-top']")) return;
    panRef.current = { active: true, x: e.clientX, y: e.clientY, sl: vp.scrollLeft, st: vp.scrollTop };
    vp.classList.add("is-panning");
  };
  const onPanMove = (e) => {
    if (!panRef.current.active) return;
    const vp = viewportRef.current; if (!vp) return;
    vp.scrollLeft = panRef.current.sl - (e.clientX - panRef.current.x);
    vp.scrollTop = panRef.current.st - (e.clientY - panRef.current.y);
  };
  const onPanUp = () => { panRef.current.active = false; viewportRef.current?.classList.remove("is-panning"); };



  const buildTree = () => {
    const byId = Object.fromEntries(members.map((m) => [m.id, m]));
    const children = {};
    const roots = [];
    members.forEach((m) => {
      const pid = m.supervisor_id && byId[m.supervisor_id] ? m.supervisor_id : null;
      if (pid) (children[pid] = children[pid] || []).push(m);
      else roots.push(m);
    });
    return { children, roots };
  };

  const exportAccessCsv = async () => {
    try {
      const res = await api.get("/org/access-log.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `access-history-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
  };

  const setPermission = async (m, field, value) => {
    setMembers(members.map((x) => x.id === m.id ? { ...x, [field]: value } : x));
    try {
      await api.put(`/org/members/${m.id}/permissions`, { [field]: value });
      const label = t("orgadmin.toasts.orgManagement");
      toast.success(value ? t("orgadmin.toasts.permGranted", { label, name: m.full_name }) : t("orgadmin.toasts.permRemoved", { label, name: m.full_name }));
      if (m.id === authUser?.id) refresh();
      api.get("/org/access-log").then((r) => setAccessLog(r.data)).catch(() => {});
    } catch (err) { toast.error(apiError(err)); load(); }
  };

  // Gate role changes behind an explicit confirmation dialog:
  // - GRANTING Organization Admin OR Manage Billing → unified grant confirmation.
  // - Acting user REMOVING their OWN Organization Admin role → self/owner confirmation.
  // - Removing Manage Billing from the LAST billing manager → warn first.
  const onTogglePerm = (m, field, value) => {
    if (value === true) { setConfirmPerm({ m, field, value, mode: "grant" }); return; }
    if (field === "is_org_admin" && m.id === authUser?.id) {
      setConfirmPerm({ m, field, value, mode: isOwner ? "owner-remove" : "self-remove" }); return;
    }
    setPermission(m, field, value);
  };

  const openPreview = (m) => {
    setPv({ action: "grant", is_org_admin: !m.is_org_admin, lang: m.language === "fr" ? "fr" : "en" });
    setPreviewFor(m);
  };

  useEffect(() => {
    if (!previewFor) return;
    if (!pv.is_org_admin) { setPvData({ subject: "", html: "", loading: false }); return; }
    let cancelled = false;
    setPvData((d) => ({ ...d, loading: true }));
    api.post(`/org/members/${previewFor.id}/access-preview`, pv)
      .then((r) => { if (!cancelled) setPvData({ subject: r.data.subject, html: r.data.html, loading: false }); })
      .catch(() => { if (!cancelled) setPvData({ subject: "", html: "", loading: false }); });
    return () => { cancelled = true; };
  }, [previewFor, pv]);

  const resendInvite = async (m) => {
    try {
      const res = await api.post(`/org/members/${m.id}/resend-invite`);
      if (res.data.email_sent) toast.success(t("orgadmin.toasts.inviteResentTo", { email: m.email }));
      else setTempPw({ email: m.email, pw: res.data.temp_password, emailSent: false, resent: true });
    } catch (err) { toast.error(apiError(err)); }
  };

  const onCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseCsv(String(reader.result));
        if (!rows.length) { toast.error(t("orgadmin.toasts.csvNoRows")); setCsvRows(null); return; }
        setCsvRows(rows);
        toast.success(t("orgadmin.toasts.csvParsed", { count: rows.length }));
      } catch { toast.error(t("orgadmin.toasts.csvReadError")); setCsvRows(null); }
    };
    reader.readAsText(file);
  };

  const submitBulk = async () => {
    setBulkBusy(true);
    try {
      let res;
      if (csvRows && csvRows.length) {
        res = await api.post("/org/members/import", { members: csvRows });
      } else {
        const list = bulkEmails.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
        if (list.length === 0) { toast.error(t("orgadmin.toasts.pasteEmail")); setBulkBusy(false); return; }
        res = await api.post("/org/members/bulk", { emails: list });
      }
      setBulkResult(res.data);
      if (res.data.created_count > 0) toast.success(t("orgadmin.toasts.invited", { count: res.data.created_count }));
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBulkBusy(false); }
  };

  const downloadSampleCsv = () => {
    const sample = "name,email,role\nJane Doe,jane@example.com,employee\nJohn Smith,john@example.com,supervisor\n";
    const blob = new Blob([sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "jotbay-team-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const saveInviteExpiry = async (days) => {
    setExpiryDays(days);
    try { await api.put("/org/invite-expiry", { days }); toast.success(t("orgadmin.toasts.invitesExpire", { count: days })); }
    catch (err) { toast.error(apiError(err)); }
  };

  const resendAllPending = async () => {
    try {
      const res = await api.post("/org/members/resend-all-pending");
      toast.success(t("orgadmin.toasts.resent", { count: res.data.resent }));
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const doTransfer = async () => {
    const targetId = transferPick;
    if (!targetId || !transferPassword) return;
    setTransferBusy(true);
    try {
      await api.put("/org/transfer-ownership", { user_id: targetId, password: transferPassword });
      const tgt = members.find((m) => m.id === targetId);
      toast.success(t("orgadmin.toasts.nowOwner", { name: tgt?.full_name || "" }));
      setTransferOpen(false); setTransferPick(""); setTransferPassword("");
      refresh();
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setTransferBusy(false); }
  };

  const submitRecoveryRequest = async () => {
    try {
      await api.post("/org/primary-admin/recovery-request", { note: "" });
      toast.success(t("orgadmin.recoveryRequested"));
    } catch (err) { toast.error(apiError(err)); }
  };

  const openRename = () => { setNameInput(settings?.name || ""); setRenameOpen(true); };
  const saveOrgName = async () => {
    const name = nameInput.trim();
    if (!name) { toast.error(t("orgadmin.orgNameBlank")); return; }
    setNameBusy(true);
    try {
      const res = await api.put("/org/name", { name });
      setSettings((s) => ({ ...s, name: res.data.name }));
      toast.success(t("orgadmin.orgNameUpdated"));
      setRenameOpen(false);
      refresh();
    } catch (err) { toast.error(apiError(err)); }
    finally { setNameBusy(false); }
  };

  const isOwner = authUser?.id && settings?.admin_user_id === authUser.id;
  const isAdmin = authUser?.can_manage_org === true || authUser?.is_primary_admin === true || authUser?.is_org_admin === true;
  const unverified = authUser?.email_verified === false;
  const visibleMembers = members.filter((m) =>
    (!pendingOnly || m.invite_pending) &&
    (langFilter === "all" || (m.language || "en") === langFilter));
  const pendingCount = members.filter((m) => m.invite_pending).length;
  const langCounts = SUPPORTED_LANGUAGES.reduce((acc, l) => {
    acc[l.code] = members.filter((m) => (m.language || "en") === l.code).length;
    return acc;
  }, {});

  const canManageOrg = authUser?.account_type === "organization" && (authUser?.can_manage_org === true || authUser?.is_primary_admin === true || authUser?.is_org_admin === true);
  if (authUser && !canManageOrg) return <AccessDenied />;

  const adminCount = members.filter((m) => m.is_org_admin || m.is_primary_admin).length;

  if (loadError) return (
    <div className="animate-fade-up max-w-lg" data-testid="org-load-error">
      <div className="rounded-xl border border-red-200 bg-red-50 p-6">
        <h2 className="font-display text-lg font-semibold text-navy mb-1 flex items-center gap-2"><Building2 className="h-5 w-5 text-red-500" /> {t("orgadmin.loadErrorTitle")}</h2>
        <p className="text-sm text-slatebody mb-4" data-testid="org-error-message">{loadError}</p>
        <Button variant="outline" onClick={load} data-testid="org-retry-btn">{t("orgadmin.retry")}</Button>
      </div>
    </div>
  );
  if (!settings) return <p className="text-slatebody" data-testid="org-loading">{t("orgadmin.loading")}</p>;

  return (
    <div className="animate-fade-up">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-navy flex items-center gap-2">
          <Building2 className="h-7 w-7 text-[#202123]" /> {settings.name}
          {isAdmin && (
            <button onClick={openRename} className="text-slatebody hover:text-[#202123] p-1.5 rounded-md hover:bg-[#ECECEC] transition-colors" title={t("orgadmin.editOrgName")} data-testid="edit-org-name-btn"><Pencil className="h-4 w-4" /></button>
          )}
        </h1>
        <p className="text-slatebody mt-1"><span className="font-mono">{settings.member_count}</span> {t("orgadmin.memberWord", { count: settings.member_count })} · <span className="font-mono">{settings.seat_count}</span> {t("orgadmin.seatWord", { count: settings.seat_count })}</p>
        {billingHealth && (() => {
          const state = billingHealth.suspended ? "red" : billingHealth.past_due ? "amber" : "green";
          const map = {
            green: { dot: "bg-green-500", cls: "border-green-200 bg-green-50 text-green-800", label: t("orgadmin.billingHealthy") },
            amber: { dot: "bg-amber-500", cls: "border-amber-200 bg-amber-50 text-amber-800", label: t("orgadmin.billingPastDue") },
            red: { dot: "bg-red-500", cls: "border-red-200 bg-red-50 text-red-800", label: t("orgadmin.billingSuspended") },
          }[state];
          return (
            <div className={`mt-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${map.cls}`} data-testid="billing-health-badge" title={t("orgadmin.billingHealthTitle")}>
              <span className={`h-2 w-2 rounded-full ${map.dot}`} />
              {map.label}
            </div>
          );
        })()}
      </div>

      <Tabs defaultValue="members">
        <TabsList className="mb-6">
          <TabsTrigger value="members" data-testid="tab-members">{t("orgadmin.tabPeople")}</TabsTrigger>
          {isAdmin && <TabsTrigger value="structure" data-testid="tab-structure">{t("orgadmin.tabStructure")}</TabsTrigger>}
          {isAdmin && <TabsTrigger value="templates" data-testid="tab-templates">{t("orgTpl.tab")}</TabsTrigger>}
          <TabsTrigger value="policy" data-testid="tab-policy">{t("orgadmin.tabDigest")}</TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          <div className="mb-4 grid grid-cols-2 gap-3" data-testid="roles-summary">
            {[
              { key: "members", label: t("orgadmin.summaryMembers"), value: members.length, color: "text-navy" },
              { key: "admins", label: t("orgadmin.summaryOrgAdmins"), value: adminCount, color: "text-[#202123]" },
            ].map((s) => (
              <div key={s.key} className="rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={`summary-${s.key}`}>
                <p className={`font-display text-2xl font-semibold ${s.color}`}>{s.value}</p>
                <p className="text-[11px] font-mono uppercase tracking-wide text-slatebody mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
          {members.length <= 1 && (
            <div className="mb-4 rounded-xl border border-[#C5C5D0]/30 bg-accent/50 p-4 flex items-center justify-between gap-4" data-testid="invite-team-nudge">
              <div>
                <p className="text-sm font-medium text-navy">{t("orgadmin.nudgeTitle")}</p>
                <p className="text-xs text-slatebody mt-0.5">{t("orgadmin.nudgeText")}</p>
              </div>
              <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-1.5 shrink-0" onClick={() => setAdding(true)} disabled={unverified} data-testid="nudge-add-member-btn"><UserPlus className="h-4 w-4" /> {t("orgadmin.inviteMember")}</Button>
            </div>
          )}
          <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-lg font-semibold text-navy flex items-center gap-2"><Users className="h-5 w-5 text-[#202123]" /> {t("orgadmin.teamMembers")}</h2>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5 text-sm text-slatebody">
                  <span>{t("orgadmin.invitesExpireIn")}</span>
                  <Select value={String(expiryDays)} onValueChange={(v) => saveInviteExpiry(Number(v))}>
                    <SelectTrigger className="h-8 w-[96px]" data-testid="invite-expiry-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{[3, 7, 14].map((d) => <SelectItem key={d} value={String(d)}>{t("orgadmin.daysOption", { count: d })}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {pendingCount > 0 && (
                  <Button variant="outline" className="gap-1.5" onClick={resendAllPending} data-testid="resend-all-btn"><Send className="h-4 w-4" /> {t("orgadmin.resendAllPending", { count: pendingCount })}</Button>
                )}
                <label className="flex items-center gap-2 text-sm text-slatebody cursor-pointer select-none" data-testid="pending-only-toggle">
                  <Checkbox checked={pendingOnly} onCheckedChange={(v) => setPendingOnly(!!v)} data-testid="pending-only-checkbox" /> {t("orgadmin.pendingOnly")}
                </label>
                <Select value={langFilter} onValueChange={setLangFilter}>
                  <SelectTrigger className="h-8 w-[150px] text-sm" data-testid="lang-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("orgadmin.allLanguages")}</SelectItem>
                    {SUPPORTED_LANGUAGES.map((l) => (
                      <SelectItem key={l.code} value={l.code}>{t(`language.${l.code}`)} ({langCounts[l.code] || 0})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" className="gap-1.5" onClick={() => { setBulkResult(null); setBulkEmails(""); setCsvRows(null); setBulkOpen(true); }} disabled={unverified} data-testid="bulk-invite-btn"><MailPlus className="h-4 w-4" /> {t("orgadmin.bulkInvite")}</Button>
                {isOwner && <Button variant="outline" className="gap-1.5" onClick={() => { setTransferPick(""); setTransferPassword(""); setTransferOpen(true); }} data-testid="transfer-primary-btn"><Crown className="h-4 w-4" /> {t("orgadmin.transferPrimary")}</Button>}
                {!isOwner && isAdmin && <Button variant="outline" className="gap-1.5" onClick={submitRecoveryRequest} data-testid="recovery-request-btn"><Crown className="h-4 w-4" /> {t("orgadmin.requestPrimary")}</Button>}
                <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-1.5" onClick={() => setAdding(true)} disabled={unverified} data-testid="add-member-btn"><UserPlus className="h-4 w-4" /> {t("orgadmin.addMember")}</Button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="members-table">
                <thead><tr className="text-left font-mono text-[11px] uppercase text-slatebody border-b border-slate-100">
                  <th className="p-2.5">{t("orgadmin.colName")}</th><th className="p-2.5">{t("orgadmin.colTitle")}</th><th className="p-2.5">{t("orgadmin.colGrade")}</th><th className="p-2.5">{t("orgadmin.colReportsTo")}</th><th className="p-2.5">{t("orgadmin.colLanguage")}</th>{isAdmin && <th className="p-2.5">{t("orgadmin.colAccess")}</th>}<th></th>
                </tr></thead>
                <tbody>
                  {visibleMembers.length === 0 && (
                    <tr><td colSpan={7} className="p-4 text-center text-slatebody text-sm" data-testid="no-members-row">{pendingOnly ? t("orgadmin.noPending") : t("orgadmin.noMembers")}</td></tr>
                  )}
                  {visibleMembers.map((m) => (
                    <tr key={m.id} className="border-b border-slate-50" data-testid={`member-${m.id}`}>
                      <td className="p-2.5">
                        <p className="font-medium text-navy flex items-center gap-2 flex-wrap">
                          {m.full_name}
                          {m.email_verified
                            ? <BadgeCheck className="h-3.5 w-3.5 text-[#202123] shrink-0" title={t("orgadmin.verified")} data-testid={`verified-${m.id}`} />
                            : <MailWarning className="h-3.5 w-3.5 text-amber-500 shrink-0" title={t("orgadmin.pendingVerification")} data-testid={`unverified-${m.id}`} />}
                          {m.id === settings.admin_user_id && <span className="text-[10px] uppercase tracking-wide font-mono px-1.5 py-0.5 rounded bg-[#ECECEC]/15 text-[#202123] flex items-center gap-1" data-testid={`owner-${m.id}`}><Crown className="h-3 w-3" /> {t("orgadmin.owner")}</span>}
                          {m.invite_pending && (m.invite_expires && new Date(m.invite_expires) < new Date()
                            ? <span className="text-[10px] uppercase tracking-wide font-mono px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid={`expired-${m.id}`}>{t("orgadmin.inviteExpired")}</span>
                            : <span className="text-[10px] uppercase tracking-wide font-mono px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" data-testid={`pending-${m.id}`}>{t("orgadmin.invitePending")}</span>)}
                          {!m.invite_pending && <span className="text-[10px] uppercase tracking-wide font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" data-testid={`logged-in-${m.id}`}>{t("orgadmin.memberLoggedIn")}</span>}
                          {m.id !== settings.admin_user_id && m.is_org_admin && <span className="text-[10px] uppercase tracking-wide font-mono px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700" data-testid={`badge-org-${m.id}`}>{t("orgadmin.orgAdminBadge")}</span>}
                        </p>
                        <p className="text-[11px] text-slatebody font-mono">{m.email}</p>
                      </td>
                      <td className="p-2.5">
                        <Select value={m.title || "__none"} onValueChange={(v) => updateMember(m.id, "title", v === "__none" ? "" : v)}>
                          <SelectTrigger className="h-8 w-44" data-testid={`title-${m.id}`}><SelectValue placeholder={t("orgadmin.noTitle")} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">{t("orgadmin.noTitle")}</SelectItem>
                            {titleLadder.map((rung) => <SelectItem key={rung.title} value={rung.title}>{rung.title}</SelectItem>)}
                            {m.title && !titleLadder.some((r) => r.title === m.title) && <SelectItem value={m.title}>{m.title}</SelectItem>}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2.5">
                        <span className="text-sm text-slatebody font-mono" data-testid={`grade-${m.id}`}>{gradeForTitle(m.title) || "—"}</span>
                      </td>
                      <td className="p-2.5">
                        <Select value={m.supervisor_id || "__none"} onValueChange={(v) => attemptReorg(m.id, v === "__none" ? null : v, buildTree().children)}>
                          <SelectTrigger className="h-8 w-40" data-testid={`reports-to-${m.id}`}><SelectValue placeholder={t("orgadmin.reportsToNone")} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">{t("orgadmin.reportsToNone")}</SelectItem>
                            {members.filter((x) => x.id !== m.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.full_name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-2.5">
                        <Select value={m.language || "en"} onValueChange={(v) => updateMember(m.id, "language", v)}>
                          <SelectTrigger className="h-8 w-32" data-testid={`language-${m.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SUPPORTED_LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{t(`language.${l.code}`)}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      {isAdmin && (
                        <td className="p-2.5">
                          {m.is_primary_admin || m.id === settings.admin_user_id ? (
                            <span className="text-[11px] text-slatebody font-mono">{t("orgadmin.fullAdmin")}</span>
                          ) : (
                            <div className="flex flex-col gap-1.5">
                              <label className="flex items-center gap-2 text-xs text-slatebody cursor-pointer" data-testid={`perm-org-${m.id}`}>
                                <Checkbox checked={m.is_org_admin === true} onCheckedChange={(v) => onTogglePerm(m, "is_org_admin", v === true)} /> {t("orgadmin.orgAdminPerm")}
                              </label>
                              <button type="button" onClick={() => openPreview(m)} className="text-[11px] text-slatebody underline underline-offset-2 hover:text-navy text-left mt-0.5" data-testid={`preview-email-${m.id}`}>{t("orgadmin.previewEmail")}</button>
                            </div>
                          )}
                        </td>
                      )}
                      <td className="p-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {m.invite_pending && (
                            <button onClick={() => resendInvite(m)} className="text-slatebody hover:text-[#202123] p-1.5" title={t("orgadmin.resendInviteEmail")} data-testid={`resend-${m.id}`}><Send className="h-4 w-4" /></button>
                          )}
                          {isAdmin && reportCount(m.id) > 0 && (
                            <button onClick={() => { setMoveTeamTarget(m); setMoveTeamTo(""); }} className="text-slatebody hover:text-[#202123] p-1.5" title={t("orgadmin.moveTeam")} data-testid={`move-team-${m.id}`}><Network className="h-4 w-4" /></button>
                          )}
                          <button onClick={() => removeMember(m.id)} className="text-slatebody hover:text-red-500 p-1.5" title={t("orgadmin.removeMember")} data-testid={`remove-${m.id}`}><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {ownershipLog.length > 0 && (
              <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="ownership-log">
                <h3 className="font-display text-sm font-semibold text-navy flex items-center gap-2 mb-3"><Crown className="h-4 w-4 text-[#202123]" /> {t("orgadmin.ownershipHistory")}</h3>
                <ul className="space-y-2">
                  {ownershipLog.map((e, i) => (
                    <li key={i} className="flex items-center justify-between text-sm border-b border-slate-50 pb-2 last:border-0 last:pb-0" data-testid={`ownership-log-${i}`}>
                      <span className="text-navy"><span className="text-slatebody">{e.from_name}</span> → <span className="font-medium">{e.to_name}</span></span>
                      <span className="text-xs text-slatebody">{new Date(e.at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {isOwner && recoveryRequests.length > 0 && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-6" data-testid="recovery-requests">
                <h3 className="font-display text-sm font-semibold text-navy flex items-center gap-2 mb-3"><Crown className="h-4 w-4 text-amber-600" /> {t("orgadmin.recoveryRequestsTitle")}</h3>
                <ul className="space-y-2">
                  {recoveryRequests.map((e, i) => (
                    <li key={i} className="flex items-center justify-between text-sm border-b border-amber-100 pb-2 last:border-0 last:pb-0" data-testid={`recovery-request-${i}`}>
                      <span className="text-navy"><span className="font-medium">{e.requester_name}</span> <span className="text-slatebody">({e.requester_email})</span></span>
                      <span className="text-xs text-slatebody">{new Date(e.at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {accessLog.length > 0 && (
              <div className="mt-4">
                {!showAccessHistory ? (
                  <button onClick={() => setShowAccessHistory(true)} className="inline-flex items-center gap-2 text-sm font-medium text-[#202123] hover:underline" data-testid="view-access-history-btn">
                    <ShieldCheck className="h-4 w-4" /> {t("orgadmin.viewAccessHistory")}
                  </button>
                ) : (
                  <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="access-log">
                    <TooltipProvider delayDuration={150}>
                      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                        <div className="flex items-center gap-3">
                          <h3 className="font-display text-sm font-semibold text-navy flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#202123]" /> {t("orgadmin.accessHistory")}</h3>
                          <button onClick={() => setShowAccessHistory(false)} className="text-xs text-slatebody hover:text-[#202123] hover:underline" data-testid="hide-access-history-btn">{t("orgadmin.hideAccessHistory")}</button>
                        </div>
                        <div className="flex items-center gap-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={exportAccessCsv} data-testid="access-export-csv"><Download className="h-3.5 w-3.5" /> {t("orgadmin.exportCsv")}</Button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[240px]" data-testid="tip-export-csv">{t("orgadmin.tipExportCsv")}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <Select value={accessFilterMember} onValueChange={setAccessFilterMember}>
                                  <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="access-filter-member"><SelectValue placeholder={t("orgadmin.allMembers")} /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="all">{t("orgadmin.allMembers")}</SelectItem>
                                    {[...new Map(accessLog.filter((e) => e.member_id).map((e) => [e.member_id, e.member_name])).entries()].map(([id, name]) => (
                                      <SelectItem key={id} value={id}>{name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[240px]" data-testid="tip-all-members">{t("orgadmin.tipAllMembers")}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <Select value={accessFilterScope} onValueChange={setAccessFilterScope}>
                                  <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="access-filter-scope"><SelectValue placeholder={t("orgadmin.allPermissions")} /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="all">{t("orgadmin.allPermissions")}</SelectItem>
                                    <SelectItem value="is_org_admin">{t("orgadmin.orgAdminPerm")}</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[240px]" data-testid="tip-all-permissions">{t("orgadmin.tipAllPermissions")}</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </TooltipProvider>
                    <ul className="space-y-2">
                      {(() => {
                        const filtered = accessLog.filter((e) =>
                          (accessFilterMember === "all" || e.member_id === accessFilterMember) &&
                          (accessFilterScope === "all" || e.scope === accessFilterScope));
                        if (filtered.length === 0) return <li className="text-sm text-slatebody py-2" data-testid="access-log-empty">{t("orgadmin.noMatchingAccess")}</li>;
                        return filtered.map((e, i) => (
                          <li key={i} className="flex items-center justify-between gap-3 text-sm border-b border-slate-50 pb-2 last:border-0 last:pb-0" data-testid={`access-log-${i}`}>
                            {e.type === "rename" ? (
                              <span className="text-navy min-w-0">
                                <span className="text-slatebody font-medium">{t("orgadmin.renamed")}</span>
                                {" "}<span className="text-slatebody">{t("orgadmin.renamedFromTo", { from: e.old_name || "—", to: e.new_name })}</span>
                                <span className="text-xs text-slatebody"> · {t("orgadmin.by", { name: e.actor_name })}</span>
                              </span>
                            ) : (
                              <span className="text-navy min-w-0">
                                <span className={e.granted ? "text-emerald-600 font-medium" : "text-slatebody font-medium"}>{e.granted ? t("orgadmin.granted") : t("orgadmin.removed")}</span>
                                {" "}<span className="text-slatebody">{t("orgadmin.orgAdminPerm")}</span>
                                {" "}{e.granted ? t("orgadmin.to") : t("orgadmin.from")} <span className="font-medium">{e.member_name}</span>
                                <span className="text-xs text-slatebody"> · {t("orgadmin.by", { name: e.actor_name })}</span>
                              </span>
                            )}
                            <span className="text-xs text-slatebody shrink-0">{new Date(e.at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
                          </li>
                        ));
                      })()}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="templates">
            <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-8">
              <OrgTemplates />
            </div>
          </TabsContent>
        )}


        <TabsContent value="policy">
          <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-8">
            <h2 className="font-display text-lg font-semibold text-navy mb-1">{t("orgadmin.execDigestTitle")}</h2>
            <p className="text-sm text-slatebody mb-4">{t("orgadmin.execDigestDesc")}</p>
            <div className="rounded-lg border border-slate-100 bg-background p-4 mb-5" data-testid="digest-empty-msg">
              <p className="text-sm text-slatebody leading-relaxed max-w-2xl">{t("orgadmin.digestEmptyMsg")}</p>
            </div>
            <div className="mb-4 flex items-center gap-3" data-testid="digest-cadence">
              <span className="text-sm text-navy">{t("orgadmin.cadence")}</span>
              {["monthly", "weekly"].map((cad) => (
                <button key={cad} onClick={() => setDigestCadence(cad)} data-testid={`cadence-${cad}`}
                  className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${digestCadence === cad ? "border-[#C5C5D0] bg-accent text-accent-foreground" : "border-slate-300 text-slatebody hover:border-slate-400"}`}>
                  {t(`orgadmin.${cad}`)}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-2.5" data-testid="digest-recipients">
              {members.map((m) => (
                <label key={m.id} className="flex items-center gap-3 rounded-md border border-slate-100 px-3.5 py-3 cursor-pointer hover:border-slate-200" data-testid={`digest-toggle-${m.id}`}>
                  <Checkbox checked={digestRecipients.includes(m.id)} onCheckedChange={() => toggleRecipient(m.id)} />
                  <div className="min-w-0"><p className="text-sm text-navy truncate">{m.full_name}</p><p className="text-[11px] text-slatebody font-mono truncate">{m.email}</p></div>
                </label>
              ))}
            </div>
            <div className="flex items-center gap-3 mt-4">
              <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" onClick={saveRecipients} data-testid="save-recipients-btn">{t("orgadmin.saveDigestSettings")}</Button>
              <Button variant="outline" onClick={loadDigestPreview} disabled={digestPreviewLoading} data-testid="digest-preview-btn">{digestPreviewLoading ? t("orgadmin.loading") : t("orgadmin.previewDigest")}</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="structure" className="space-y-6">
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50" data-testid="structure-subnav">
            {[["titles", "titleLadderTitle"], ["chart", "orgChartTitle"]].map(([key, lbl]) => (
              <button key={key} type="button" onClick={() => setStructureSection(key)}
                className={`px-3.5 py-1.5 text-sm rounded-md transition-colors ${structureSection === key ? "bg-white text-navy shadow-sm font-medium" : "text-slatebody hover:text-navy"}`}
                data-testid={`structure-subnav-${key}`}>{t(`orgadmin.${lbl}`)}</button>
            ))}
          </div>
          <div className={`rounded-xl border border-slate-200 bg-white p-6 md:p-8 ${structureSection !== "titles" ? "hidden" : ""}`} data-testid="title-ladder-editor">
            <h2 className="font-display text-lg font-semibold text-navy mb-1 flex items-center gap-2"><Network className="h-5 w-5 text-[#202123]" /> {t("orgadmin.titleLadderTitle")}</h2>
            <p className="text-sm text-slatebody mb-5 max-w-2xl">{t("orgadmin.titleLadderDesc")}</p>
            <div className="hidden sm:flex items-center gap-2 mb-1.5 pl-8 max-w-2xl">
              <span className="text-[11px] font-mono uppercase text-slatebody flex-1">{t("orgadmin.colTitle")}</span>
              <span className="text-[11px] font-mono uppercase text-slatebody w-40">{t("orgadmin.colGrade")}</span>
              <span className="w-[104px] shrink-0" />
            </div>
            <ul className="space-y-2 mb-4">
              {titleLadder.length === 0 && <li className="text-sm text-slatebody" data-testid="no-titles">{t("orgadmin.noTitles")}</li>}
              {titleLadder.map((rung, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2 max-w-2xl" data-testid={`title-row-${i}`}>
                  <span className="font-mono text-[11px] text-slatebody w-6 shrink-0">{`#${i + 1}`}</span>
                  <Input value={rung.title} onChange={(e) => updateRung(i, "title", e.target.value)} placeholder={t("orgadmin.titlePlaceholder")} className="h-9 flex-1 min-w-[10rem]" data-testid={`title-input-${i}`} />
                  <Input value={rung.grade || ""} onChange={(e) => updateRung(i, "grade", e.target.value)} placeholder={t("orgadmin.gradePlaceholder")} className="h-9 w-40" data-testid={`grade-input-${i}`} />
                  <button onClick={() => moveTitle(i, -1)} disabled={i === 0} className="p-1.5 text-slatebody hover:text-[#202123] disabled:opacity-30" data-testid={`title-up-${i}`}><ChevronUp className="h-4 w-4" /></button>
                  <button onClick={() => moveTitle(i, 1)} disabled={i === titleLadder.length - 1} className="p-1.5 text-slatebody hover:text-[#202123] disabled:opacity-30" data-testid={`title-down-${i}`}><ChevronDown className="h-4 w-4" /></button>
                  <button onClick={() => setTitleLadder(titleLadder.filter((_, idx) => idx !== i))} className="p-1.5 text-slatebody hover:text-red-500" data-testid={`title-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
            <div className="flex gap-2 mb-5 max-w-sm">
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addTitle(); }} placeholder={t("orgadmin.titlePlaceholder")} className="h-9" data-testid="new-title-input" />
              <Button variant="outline" onClick={addTitle} data-testid="add-title-btn">{t("orgadmin.addTitle")}</Button>
            </div>
            <div className="flex items-center gap-3">
              <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" onClick={saveTitles} data-testid="save-titles-btn">{t("orgadmin.saveTitles")}</Button>
              <p className="text-xs text-slatebody">{t("orgadmin.titleLadderNote")}</p>
            </div>
          </div>

          <div className={`rounded-xl border border-slate-200 bg-white p-6 md:p-8 ${structureSection !== "chart" ? "hidden" : ""}`} data-testid="org-chart">
            <div className="flex items-start justify-between gap-4 mb-1">
              <h2 className="font-display text-lg font-semibold text-navy flex items-center gap-2"><Network className="h-5 w-5 text-[#202123]" /> {t("orgadmin.orgChartTitle")}</h2>
              {isAdmin && (
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" className="h-8" onClick={toggleCollapseAll} data-testid="chart-collapse-all">{collapsed.size > 0 ? t("orgadmin.expandAll") : t("orgadmin.collapseAll")}</Button>
                  <div className="flex items-center gap-1 mr-1 rounded-md border border-slate-200 p-0.5" data-testid="chart-zoom-controls">
                    <button type="button" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))} className="h-7 w-7 flex items-center justify-center rounded text-slatebody hover:bg-slate-100 disabled:opacity-30" disabled={zoom <= 0.5} data-testid="chart-zoom-out" aria-label={t("orgadmin.zoomOut")}><Minus className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => setZoom(1)} className="h-7 px-1.5 flex items-center justify-center rounded text-[11px] font-mono text-slatebody hover:bg-slate-100 min-w-[3rem]" data-testid="chart-zoom-fit" title={t("orgadmin.zoomFit")}>{Math.round(zoom * 100)}%</button>
                    <button type="button" onClick={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)))} className="h-7 w-7 flex items-center justify-center rounded text-slatebody hover:bg-slate-100 disabled:opacity-30" disabled={zoom >= 2} data-testid="chart-zoom-in" aria-label={t("orgadmin.zoomIn")}><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                  <Button variant="outline" size="sm" className="h-8 gap-1.5" disabled={exporting} onClick={() => exportChart("png")} data-testid="chart-export-png"><Download className="h-3.5 w-3.5" /> PNG</Button>
                  <Button variant="outline" size="sm" className="h-8 gap-1.5" disabled={exporting} onClick={() => exportChart("pdf")} data-testid="chart-export-pdf"><Download className="h-3.5 w-3.5" /> PDF</Button>
                </div>
              )}
            </div>
            <p className="text-sm text-slatebody mb-3 max-w-2xl">{t("orgadmin.orgChartDesc")}</p>
            {isAdmin && <p className="text-xs text-slatebody mb-4 rounded-md bg-slate-50 border border-slate-200 px-3 py-2" data-testid="chart-drag-hint">{t("orgadmin.dragHint")}</p>}
            <div className="relative mb-4 max-w-xs">
              <Search className="h-4 w-4 text-slatebody absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input value={chartSearch} onChange={(e) => {
                const v = e.target.value; setChartSearch(v);
                const q = v.trim().toLowerCase();
                if (q) setTimeout(() => {
                  const m = members.find((x) => (x.full_name || "").toLowerCase().includes(q) || (x.title || "").toLowerCase().includes(q));
                  if (m) { const el = document.querySelector(`[data-testid="chart-node-${m.id}"]`); if (el) el.scrollIntoView({ behavior: "smooth", block: "center" }); }
                }, 60);
              }} placeholder={t("orgadmin.chartSearchPlaceholder")} className="h-9 pl-8" data-testid="chart-search" />
            </div>
            <div ref={viewportRef} className="overflow-auto rounded-lg chart-pan" style={{ maxHeight: "62vh" }} data-testid="chart-viewport"
              onMouseDown={onPanDown} onMouseMove={onPanMove} onMouseUp={onPanUp} onMouseLeave={onPanUp}>
            <div ref={chartRef} className="bg-white inline-block min-w-full p-2" style={{ transform: `scale(${zoom})`, transformOrigin: "top left", width: `${100 / zoom}%` }}>
            {(() => {
              const { children, roots } = buildTree();
              const focusMember = focusId ? members.find((x) => x.id === focusId) : null;
              const effectiveRoots = focusMember ? [focusMember] : roots;
              const q = chartSearch.trim().toLowerCase();
              const searching = q.length > 0;
              const matches = (m) => searching && ((m.full_name || "").toLowerCase().includes(q) || (m.title || "").toLowerCase().includes(q));
              const dragProps = (m) => isAdmin ? {
                draggable: true,
                onDragStart: (e) => { dragIdRef.current = m.id; setDragId(m.id); e.dataTransfer.effectAllowed = "move"; },
                onDragEnd: () => { dragIdRef.current = null; setDragId(null); setDropTarget(null); },
                onDragOver: (e) => { const d = dragIdRef.current; if (d && d !== m.id) { e.preventDefault(); setDropTarget(m.id); } },
                onDragLeave: () => setDropTarget((d) => (d === m.id ? null : d)),
                onDrop: (e) => { e.preventDefault(); const d = dragIdRef.current; if (d && d !== m.id) attemptReorg(d, m.id, children); dragIdRef.current = null; setDropTarget(null); setDragId(null); },
              } : {};
              const renderNode = (m) => {
                const kids = children[m.id] || [];
                const hasKids = kids.length > 0;
                const isCollapsed = !searching && collapsed.has(m.id);
                return (
                  <li className="orgchart-li" key={m.id}>
                    <div className={`orgchart-node ${isAdmin ? "is-draggable" : ""} ${matches(m) ? "is-match" : ""} ${dropTarget === m.id ? "is-drop" : ""} ${dragId === m.id ? "is-drag" : ""} ${focusId === m.id ? "is-focus" : ""}`}
                      data-testid={`chart-node-${m.id}`} {...dragProps(m)}
                      onClick={() => { if (isAdmin) setFocusId(m.id); }} title={isAdmin ? t("orgadmin.focusHint") : undefined}>
                      <div className="orgchart-name">{m.full_name}</div>
                      <div className="orgchart-metaline">
                        {m.title && <span className="orgchart-title-badge">{m.title}</span>}
                        {m.is_primary_admin ? <span className="orgchart-role-badge">{t("orgadmin.primaryAdminBadge")}</span>
                          : m.is_org_admin ? <span className="orgchart-role-badge">{t("orgadmin.orgAdminBadge")}</span> : null}
                      </div>
                      {hasKids && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); toggleCollapse(m.id); }} disabled={searching}
                          className="orgchart-toggle" data-testid={`chart-collapse-${m.id}`} aria-label={t("orgadmin.chartToggleBranch")}>
                          {isCollapsed ? `+${kids.length}` : <ChevronDown className="h-3 w-3" />}
                        </button>
                      )}
                    </div>
                    {hasKids && !isCollapsed && <ul>{kids.map((c) => renderNode(c))}</ul>}
                  </li>
                );
              };
              return (
                <>
                  {focusMember && (
                    <div className="mb-4 flex items-center justify-between gap-3 rounded-md bg-teal-50 border border-teal px-3 py-2 text-sm" data-testid="chart-focus-banner">
                      <span className="text-navy">{t("orgadmin.focusedOn", { name: focusMember.full_name })}</span>
                      <button type="button" onClick={() => setFocusId(null)} className="text-teal font-medium hover:underline" data-testid="chart-focus-exit">{t("orgadmin.showFullChart")}</button>
                    </div>
                  )}
                  {isAdmin && !focusMember && (
                    <div
                      className={`mb-4 rounded-md border border-dashed px-3 py-2.5 text-xs text-slatebody transition-colors ${dropTarget === "__top" ? "bg-teal-50 border-teal ring-1 ring-teal" : "border-slate-300"}`}
                      data-testid="chart-drop-top"
                      onDragOver={(e) => { if (dragIdRef.current) { e.preventDefault(); setDropTarget("__top"); } }}
                      onDragLeave={() => setDropTarget((d) => (d === "__top" ? null : d))}
                      onDrop={(e) => { e.preventDefault(); const d = dragIdRef.current; if (d) reorg(d, null, children); dragIdRef.current = null; setDropTarget(null); setDragId(null); }}>
                      {t("orgadmin.dropTop")}
                    </div>
                  )}
                  {effectiveRoots.length
                    ? <div className="orgchart"><ul>{effectiveRoots.map((r) => renderNode(r))}</ul></div>
                    : <p className="text-sm text-slatebody" data-testid="chart-empty">{t("orgadmin.noMembers")}</p>}
                </>
              );
            })()}
            </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!confirmPerm} onOpenChange={(o) => { if (!o) setConfirmPerm(null); }}>
        <DialogContent className="bg-white" data-testid="confirm-perm-dialog">
          {(() => {
            if (!confirmPerm) return null;
            const { m, field, value, mode } = confirmPerm;
            const orgName = settings?.name || "";
            const permLabel = t("orgadmin.permOrgAdminAccess");
            let title, body, yesLabel, testid;
            if (mode === "grant") {
              title = t("orgadmin.confirmGrantTitle");
              body = t("orgadmin.confirmGrantBody", { name: m.full_name, perm: permLabel, org: orgName });
              yesLabel = t("orgadmin.confirmGrantYes");
              testid = "confirm-grant-btn";
            } else if (mode === "owner-remove") {
              title = t("orgadmin.confirmOwnerRemoveTitle");
              body = t("orgadmin.confirmOwnerRemoveBody");
              yesLabel = t("orgadmin.confirmOwnerRemoveYes");
              testid = "confirm-owner-remove-btn";
            } else {
              title = t("orgadmin.confirmSelfRemoveTitle");
              body = t("orgadmin.confirmSelfRemoveBody");
              yesLabel = t("orgadmin.confirmSelfRemoveYes");
              testid = "confirm-self-remove-btn";
            }
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display">{title}</DialogTitle>
                  <DialogDescription data-testid="confirm-perm-body">{body}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setConfirmPerm(null)} data-testid="confirm-perm-cancel">{t("orgadmin.confirmKeep")}</Button>
                  <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]"
                    onClick={() => { setPermission(m, field, value); setConfirmPerm(null); }} data-testid={testid}>{yesLabel}</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewFor} onOpenChange={(o) => { if (!o) setPreviewFor(null); }}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-auto" data-testid="preview-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{t("orgadmin.previewTitle")}</DialogTitle>
            <DialogDescription>{previewFor && t("orgadmin.previewDesc", { name: previewFor.full_name })}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 items-start">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slatebody mb-1">{t("orgadmin.previewAction")}</div>
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                  {["grant", "remove"].map((a) => (
                    <button key={a} type="button" onClick={() => setPv((p) => ({ ...p, action: a }))}
                      className={`px-3 py-1.5 text-xs ${pv.action === a ? "bg-navy text-white" : "bg-white text-slatebody hover:bg-slate-50"}`}
                      data-testid={`preview-action-${a}`}>{a === "grant" ? t("orgadmin.previewGrant") : t("orgadmin.previewRemove")}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slatebody mb-1">{t("orgadmin.previewPermissions")}</div>
                <div className="flex flex-col gap-1.5">
                  <label className="flex items-center gap-2 text-xs text-slatebody cursor-pointer">
                    <Checkbox checked={pv.is_org_admin} onCheckedChange={(v) => setPv((p) => ({ ...p, is_org_admin: v === true }))} data-testid="preview-perm-org" /> {t("orgadmin.orgAdminPerm")}
                  </label>
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slatebody mb-1">{t("orgadmin.previewLanguage")}</div>
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                  {["en", "fr"].map((l) => (
                    <button key={l} type="button" onClick={() => setPv((p) => ({ ...p, lang: l }))}
                      className={`px-3 py-1.5 text-xs uppercase ${pv.lang === l ? "bg-navy text-white" : "bg-white text-slatebody hover:bg-slate-50"}`}
                      data-testid={`preview-lang-${l}`}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
            {!pv.is_org_admin ? (
              <p className="text-sm text-slatebody" data-testid="preview-no-perm">{t("orgadmin.previewNoPerm")}</p>
            ) : (
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 text-sm" data-testid="preview-subject">
                  <span className="text-[11px] uppercase tracking-wide text-slatebody">{t("orgadmin.previewSubject")}: </span>
                  <span className="font-medium text-navy">{pvData.subject}</span>
                </div>
                <div className="p-4 text-sm text-navy" data-testid="preview-email-content"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pvData.html || "") }} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewFor(null)} data-testid="preview-email-close">{t("orgadmin.previewClose")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!moveTeamTarget} onOpenChange={(o) => { if (!o) { setMoveTeamTarget(null); setMoveTeamTo(""); } }}>
        <DialogContent className="bg-white" data-testid="move-team-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("orgadmin.moveTeamTitle")}</DialogTitle>
            <DialogDescription>{t("orgadmin.moveTeamDesc", { name: moveTeamTarget?.full_name || "", count: moveTeamTarget ? reportCount(moveTeamTarget.id) : 0 })}</DialogDescription></DialogHeader>
          <div>
            <Label>{t("orgadmin.moveTeamLabel")}</Label>
            <Select value={moveTeamTo || "__none"} onValueChange={(v) => setMoveTeamTo(v === "__none" ? "" : v)}>
              <SelectTrigger className="mt-1.5" data-testid="move-team-select"><SelectValue placeholder={t("orgadmin.reportsToNone")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">{t("orgadmin.reportsToNone")}</SelectItem>
                {members.filter((x) => x.id !== moveTeamTarget?.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMoveTeamTarget(null); setMoveTeamTo(""); }}>{t("orgadmin.cancel")}</Button>
            <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" onClick={moveTeam} data-testid="move-team-confirm">{t("orgadmin.moveTeamConfirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!removeTarget} onOpenChange={(o) => { if (!o) { setRemoveTarget(null); setReassignTo(""); setRemoveMode("vacant"); } }}>
        <DialogContent className="bg-white" data-testid="reassign-reports-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("orgadmin.reassignTitle")}</DialogTitle>
            <DialogDescription>{t("orgadmin.reassignDesc", { name: removeTarget?.full_name || "" })}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 p-3 cursor-pointer" data-testid="remove-mode-vacant">
              <input type="radio" name="remove-mode" className="mt-1" checked={removeMode === "vacant"} onChange={() => setRemoveMode("vacant")} />
              <span>
                <span className="block text-sm font-medium text-navy">{t("orgadmin.removeVacantTitle")}</span>
                <span className="block text-xs text-slatebody mt-0.5">
                  {removeTarget?.supervisor_id
                    ? t("orgadmin.removeVacantToMgr", { name: (members.find((x) => x.id === removeTarget.supervisor_id) || {}).full_name || "" })
                    : t("orgadmin.removeVacantToTop")}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 p-3 cursor-pointer" data-testid="remove-mode-reassign">
              <input type="radio" name="remove-mode" className="mt-1" checked={removeMode === "reassign"} onChange={() => setRemoveMode("reassign")} />
              <span className="flex-1">
                <span className="block text-sm font-medium text-navy">{t("orgadmin.reassignLabel")}</span>
                <Select value={reassignTo} onValueChange={(v) => { setReassignTo(v); setRemoveMode("reassign"); }}>
                  <SelectTrigger className="mt-1.5" data-testid="reassign-select"><SelectValue placeholder={t("orgadmin.reassignPlaceholder")} /></SelectTrigger>
                  <SelectContent>
                    {members.filter((x) => x.id !== removeTarget?.id).map((x) => <SelectItem key={x.id} value={x.id}>{x.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRemoveTarget(null); setReassignTo(""); setRemoveMode("vacant"); }}>{t("orgadmin.cancel")}</Button>
            <Button className="bg-red-50 hover:bg-red-100 text-red-700 border border-red-200"
              disabled={removeMode === "reassign" && !reassignTo}
              onClick={() => removeMember(removeTarget.id, removeMode === "vacant" ? { vacant: true } : { reassign: reassignTo })}
              data-testid="reassign-confirm">{t("orgadmin.reassignConfirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="bg-white" data-testid="add-member-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("orgadmin.addTeamMember")}</DialogTitle><DialogDescription>{t("orgadmin.addMemberDesc")}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("orgadmin.fullName")}</Label><Input value={newMember.full_name} onChange={(e) => setNewMember({ ...newMember, full_name: e.target.value })} className="mt-1.5" data-testid="member-name" /></div>
            <div><Label>{t("orgadmin.email")}</Label><Input type="email" value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} className="mt-1.5" data-testid="member-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("orgadmin.colTitle")}</Label>
                <Select value={newMember.title || "__none"} onValueChange={(v) => setNewMember({ ...newMember, title: v === "__none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="member-title"><SelectValue placeholder={t("orgadmin.noTitle")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">{t("orgadmin.noTitle")}</SelectItem>
                    {titleLadder.map((rung) => <SelectItem key={rung.title} value={rung.title}>{rung.title}{rung.grade ? ` · ${rung.grade}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t("orgadmin.colReportsTo")}</Label>
                <Select value={newMember.supervisor_id || "__none"} onValueChange={(v) => setNewMember({ ...newMember, supervisor_id: v === "__none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="member-reports-to"><SelectValue placeholder={t("orgadmin.reportsToNone")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">{t("orgadmin.reportsToNone")}</SelectItem>
                    {members.map((x) => <SelectItem key={x.id} value={x.id}>{x.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2 pt-1">
              <p className="text-xs font-mono uppercase tracking-wide text-slatebody">{t("orgadmin.colAccess")}</p>
              <label className="flex items-center gap-2 text-sm text-slatebody cursor-pointer" data-testid="member-perm-org">
                <Checkbox checked={newMember.is_org_admin} onCheckedChange={(v) => setNewMember({ ...newMember, is_org_admin: v === true })} /> {t("orgadmin.orgAdminPerm")}
              </label>
              <p className="text-xs text-slatebody">{t("orgadmin.permsHint")}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdding(false)}>{t("orgadmin.cancel")}</Button>
            <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" onClick={addMember} data-testid="member-save">{t("orgadmin.addMember")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tempPw} onOpenChange={(o) => !o && setTempPw(null)}>
        <DialogContent className="bg-white" data-testid="temp-pw-dialog">
          <DialogHeader><DialogTitle className="font-display">{tempPw?.resent ? t("orgadmin.inviteResent") : t("orgadmin.memberAdded")}</DialogTitle>
            <DialogDescription>{tempPw?.emailSent
              ? t("orgadmin.emailedCreds")
              : t("orgadmin.shareCreds")}</DialogDescription></DialogHeader>
          {tempPw && (
            <div className="space-y-2">
              {tempPw.emailSent && (
                <div className="rounded-md bg-accent/60 border border-[#C5C5D0]/30 px-3.5 py-2.5 text-sm text-navy" data-testid="invite-email-sent">
                  {t("orgadmin.invitationEmailed", { email: tempPw.email })}
                </div>
              )}
              {!tempPw.emailSent && (
                <>
                  <p className="text-sm text-slatebody">{t("orgadmin.email")}</p>
                  <Input readOnly value={tempPw.email} className="font-mono text-sm" />
                  <p className="text-sm text-slatebody mt-2">{t("orgadmin.temporaryPassword")}</p>
                  <div className="flex gap-2">
                    <Input readOnly value={tempPw.pw} className="font-mono text-sm" data-testid="temp-pw-value" />
                    <Button variant="outline" onClick={() => { navigator.clipboard.writeText(tempPw.pw); toast.success(t("orgadmin.toasts.copied")); }} className="gap-1.5"><Copy className="h-4 w-4" /></Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkResult(null); setCsvRows(null); } }}>
        <DialogContent className="bg-white" data-testid="bulk-invite-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("orgadmin.bulkInvite")}</DialogTitle>
            <DialogDescription>{t("orgadmin.bulkInviteDesc")}</DialogDescription></DialogHeader>
          {!bulkResult ? (
            <div className="space-y-3">
              <div>
                <Label>{t("orgadmin.emailAddresses")}</Label>
                <Textarea value={bulkEmails} onChange={(e) => setBulkEmails(e.target.value)} rows={5} placeholder="alex@company.com, sam@company.com…" className="mt-1.5" data-testid="bulk-emails-input" />
              </div>
              <p className="text-xs text-slatebody">{t("onboarding.inviteAsMemberNote")}</p>
              <div className="pt-3 border-t border-slate-100">
                <p className="text-xs text-slatebody mb-2">{t("orgadmin.csvImportPre")}<span className="font-mono">name, email, role</span>{t("orgadmin.csvImportPost")}<button type="button" onClick={downloadSampleCsv} className="text-[#202123] font-medium hover:underline" data-testid="download-sample-csv">{t("orgadmin.downloadSampleCsv")}</button></p>
                <input type="file" accept=".csv,text/csv" onChange={onCsvFile} className="block w-full text-sm text-slatebody file:mr-3 file:rounded-md file:border-0 file:bg-[#ECECEC] file:px-3 file:py-1.5 file:text-[#202123] file:cursor-pointer hover:file:bg-[#E0E0E0]" data-testid="csv-file-input" />
                {csvRows && <p className="text-xs text-[#202123] mt-2" data-testid="csv-parsed-count">{t("orgadmin.csvParsedHint", { count: csvRows.length })}</p>}
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="bulk-result">
              <div className="rounded-md bg-accent/60 border border-[#C5C5D0]/30 px-3.5 py-2.5 text-sm text-navy">
                {t("orgadmin.bulkResultInvited", { count: bulkResult.created_count })}{bulkResult.skipped_count > 0 ? t("orgadmin.bulkResultSkipped", { count: bulkResult.skipped_count }) : ""}.
              </div>
              {bulkResult.skipped_count > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-md border border-slate-100 divide-y divide-slate-50">
                  {bulkResult.skipped.map((s) => (
                    <div key={s.email} className="flex items-center justify-between px-3 py-2 text-xs">
                      <span className="font-mono text-slatebody truncate">{s.email}</span>
                      <span className="text-red-500 shrink-0 ml-2 capitalize">{s.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {!bulkResult ? (
              <>
                <Button variant="outline" onClick={() => setBulkOpen(false)}>{t("orgadmin.cancel")}</Button>
                <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" disabled={bulkBusy || (!bulkEmails.trim() && !(csvRows && csvRows.length))} onClick={submitBulk} data-testid="bulk-submit">{bulkBusy ? t("orgadmin.sendingInvites") : t("orgadmin.sendInvites")}</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setBulkResult(null); setBulkEmails(""); setCsvRows(null); }} data-testid="bulk-invite-more">{t("orgadmin.inviteMore")}</Button>
                <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" onClick={() => setBulkOpen(false)} data-testid="bulk-done">{t("orgadmin.done")}</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={(o) => { setTransferOpen(o); if (!o) { setTransferPick(""); setTransferPassword(""); } }}>
        <DialogContent className="bg-white" data-testid="transfer-ownership-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("orgadmin.transferPrimary")}</DialogTitle>
            <DialogDescription>{t("orgadmin.transferPrimaryDesc")}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("orgadmin.transferPickLabel")}</Label>
              <Select value={transferPick} onValueChange={setTransferPick}>
                <SelectTrigger className="mt-1.5" data-testid="transfer-pick"><SelectValue placeholder={t("orgadmin.transferPickPlaceholder")} /></SelectTrigger>
                <SelectContent className="bg-white">
                  {members.filter((x) => x.id !== settings.admin_user_id).map((x) => <SelectItem key={x.id} value={x.id}>{x.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("orgadmin.transferPasswordLabel")}</Label>
              <Input type="password" value={transferPassword} onChange={(e) => setTransferPassword(e.target.value)} className="mt-1.5" data-testid="transfer-password" autoComplete="current-password" />
              <p className="text-xs text-slatebody mt-1">{t("orgadmin.transferPasswordHint")}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)}>{t("orgadmin.cancel")}</Button>
            <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-1.5" disabled={transferBusy || !transferPick || !transferPassword} onClick={doTransfer} data-testid="transfer-confirm-btn"><Crown className="h-4 w-4" /> {transferBusy ? t("orgadmin.transferring") : t("orgadmin.transferConfirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={renameOpen} onOpenChange={(o) => { setRenameOpen(o); if (!o) setNameBusy(false); }}>
        <DialogContent className="bg-white" data-testid="rename-org-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("orgadmin.renameTitle")}</DialogTitle>
            <DialogDescription>{t("orgadmin.renameDesc")}</DialogDescription></DialogHeader>
          <div>
            <Label>{t("orgadmin.orgNameLabel")}</Label>
            <Input value={nameInput} onChange={(e) => setNameInput(e.target.value)} maxLength={120}
              onKeyDown={(e) => { if (e.key === "Enter" && nameInput.trim()) saveOrgName(); }}
              className="mt-1.5" autoFocus data-testid="rename-org-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>{t("orgadmin.cancel")}</Button>
            <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" disabled={nameBusy || !nameInput.trim()} onClick={saveOrgName} data-testid="rename-org-save">{nameBusy ? t("orgadmin.loading") : t("orgadmin.saveName")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingReorg} onOpenChange={(o) => !o && setPendingReorg(null)}>
        <DialogContent className="bg-white" data-testid="title-warning-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("orgadmin.titleWarnTitle")}</DialogTitle>
            <DialogDescription>{pendingReorg && t("orgadmin.titleWarnBody", { member: pendingReorg.memberName, memberTitle: pendingReorg.memberTitle, manager: pendingReorg.mgrName, managerTitle: pendingReorg.mgrTitle })}</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingReorg(null)} data-testid="title-warning-cancel">{t("orgadmin.cancel")}</Button>
            <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" data-testid="title-warning-continue"
              onClick={() => { const p = pendingReorg; setPendingReorg(null); reorg(p.memberId, p.newManagerId, p.childrenMap); }}>{t("orgadmin.titleWarnContinue")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!digestPreview} onOpenChange={(o) => !o && setDigestPreview(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-auto" data-testid="digest-preview-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("orgadmin.digestPreviewTitle")}</DialogTitle>
            <DialogDescription>{digestPreview && t("orgadmin.digestPreviewMeta", { period: digestPreview.period, cadence: t(`orgadmin.${digestPreview.cadence}`) })}</DialogDescription></DialogHeader>
          {digestPreview && (
            <div className="space-y-3">
              <div className="text-xs text-slatebody" data-testid="digest-preview-recipients">
                <span className="font-medium">{t("orgadmin.digestPreviewRecipients")}</span>{" "}
                {digestPreview.recipients.map((r) => r.name || r.email).join(", ") || t("orgadmin.digestPreviewNoRecipients")}
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-navy" data-testid="digest-preview-content"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(digestPreview.html || "") }} />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDigestPreview(null)}>{t("orgadmin.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



    </div>
  );
}
